import http from '../http-common';

const API_URL_PATH = 'analysis-runs';

/**
 * Client for the analysis-runs read model and control endpoints (M1 contract E).
 *
 * Every run is a ledger document (snake_case fields, `_id` == run_id == Celery task id).
 * Lists return RunDTOs without `config.snapshot`; `get(id)` returns the full document.
 */
class AnalysisRunsService {
  /**
   * List runs.
   * @param {{ source_type?: 'interview'|'manual_analysis'|'cv', source_id?: string|number,
   *   status?: string, group_id?: string, limit?: number }} params
   * @returns {Promise} resolves { items: RunDTO[], total }
   */
  list(params = {}) {
    const query = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query[key] = value;
      }
    });
    return http.get(API_URL_PATH, { params: query });
  }

  /**
   * Live queue snapshot: waiting / running / recent lanes, slots, health and versions.
   * @returns {Promise}
   */
  queue() {
    return http.get(`${API_URL_PATH}/queue`);
  }

  /**
   * Full run document (including config.snapshot).
   * @param {string} runId
   * @returns {Promise}
   */
  get(runId) {
    return http.get(`${API_URL_PATH}/${encodeURIComponent(runId)}`);
  }

  /**
   * Remove a run that has not started yet (QUEUED | DISPATCHED -> CANCELLED). 204 on success,
   * 409 when the run is already running or finished.
   * @param {string} runId
   * @returns {Promise}
   */
  remove(runId) {
    return http.delete(`${API_URL_PATH}/${encodeURIComponent(runId)}`);
  }

  /**
   * Cooperative cancellation of a RUNNING run (-> CANCEL_REQUESTED). 202 on success,
   * 409 when the worker is already persisting results.
   * @param {string} runId
   * @returns {Promise}
   */
  cancel(runId) {
    return http.post(`${API_URL_PATH}/${encodeURIComponent(runId)}/cancel`);
  }

  /**
   * Force-terminate a run stuck in CANCEL_REQUESTED. Requires the server-side flag; before the
   * grace period the caller must pass `confirm = true`.
   * @param {string} runId
   * @param {boolean} confirm
   * @returns {Promise}
   */
  force(runId, confirm = false) {
    const body = confirm ? { confirm: true } : {};
    return http.post(`${API_URL_PATH}/${encodeURIComponent(runId)}/force`, body);
  }

  /**
   * Mark a run whose worker stopped heartbeating as FAILED (error.type 'worker_lost').
   * @param {string} runId
   * @returns {Promise}
   */
  markFailed(runId) {
    return http.post(`${API_URL_PATH}/${encodeURIComponent(runId)}/mark-failed`);
  }

  /**
   * Make a SUCCEEDED run the active one for its source (M2 contract 3): the SQL pointer moves
   * to its result doc. 409 when the run has no result or is not SUCCEEDED. Resolves the updated
   * run summary.
   * @param {string} runId
   * @returns {Promise}
   */
  promote(runId) {
    return http.post(`${API_URL_PATH}/${encodeURIComponent(runId)}/promote`);
  }

  /**
   * Pin a run: promotes it when it is not the active one, then blocks automatic promotion of
   * later runs until unpinned. Resolves the updated run summary.
   * @param {string} runId
   * @returns {Promise}
   */
  pin(runId) {
    return http.post(`${API_URL_PATH}/${encodeURIComponent(runId)}/pin`);
  }

  /**
   * Remove the pin from the active run of the source. Resolves the updated run summary.
   * @param {string} runId
   * @returns {Promise}
   */
  unpin(runId) {
    return http.post(`${API_URL_PATH}/${encodeURIComponent(runId)}/unpin`);
  }

  /**
   * Pause the holding pen (M3 contract 7): queued runs stay QUEUED until resumed. Resolves the
   * queue settings `{ paused, paused_by, paused_at }`.
   * @returns {Promise}
   */
  pauseQueue() {
    return http.post(`${API_URL_PATH}/queue/pause`);
  }

  /**
   * Resume dispatching from the holding pen. Resolves the queue settings.
   * @returns {Promise}
   */
  resumeQueue() {
    return http.post(`${API_URL_PATH}/queue/resume`);
  }

  /**
   * Move a QUEUED run inside the waiting lane (M3 contract 7). Exactly one of `before_run_id`,
   * `after_run_id` or `position` is expected; the server assigns the new gap-numbered position.
   * 409 when the run is no longer QUEUED.
   * @param {string} runId
   * @param {{ before_run_id?: string, after_run_id?: string, position?: number }} body
   * @returns {Promise}
   */
  reorder(runId, body = {}) {
    const payload = {};
    if (body.before_run_id) payload.before_run_id = body.before_run_id;
    if (body.after_run_id) payload.after_run_id = body.after_run_id;
    if (Number.isFinite(body.position)) payload.position = body.position;
    return http.patch(`${API_URL_PATH}/${encodeURIComponent(runId)}/position`, payload);
  }

  /**
   * Two runs side by side (M4 contract 9): `GET analysis-runs/compare?a=&b=` ->
   * `{ a: RunBrief, b: RunBrief, same_source, funnel: { a, b, delta }, alerts_by_gesture,
   *   ideas: { only_in_a, only_in_b, both }, config_diff, engine_diff, timings }`.
   * @param {string} a run id shown on the left
   * @param {string} b run id shown on the right
   * @returns {Promise}
   */
  /**
   * Runs again with the stored configuration, engines and payload of a finished run (M5 X2).
   * @param {string} runId
   * @param {{ batch?: string, note?: string }} payload
   * @returns {Promise} 202 `{ run_id, replay_of, position, engines }`
   */
  replay(runId, payload = {}) {
    return http.post(`${API_URL_PATH}/${encodeURIComponent(runId)}/replay`, payload);
  }

  compare(a, b) {
    return http.get(`${API_URL_PATH}/compare`, { params: { a, b } });
  }

  /**
   * Distinct batch names of the most recent runs (newest first), used as suggestions in the
   * launch dialog. Resolves `string[]`; never rejects (an unavailable list yields `[]`).
   * @param {{ source_type?: string, limit?: number }} params
   * @returns {Promise<string[]>}
   */
  async recentBatches({ source_type, limit = 100 } = {}) {
    try {
      const response = await this.list({ source_type, limit });
      const items = response?.data?.items || response?.data?.data?.items || [];
      const seen = new Set();
      items.forEach((run) => {
        const batch = typeof run?.batch === 'string' ? run.batch.trim() : '';
        if (batch) seen.add(batch);
      });
      return Array.from(seen);
    } catch (error) {
      return [];
    }
  }
}

export default new AnalysisRunsService();
